import React, { useState, createContext, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { auth, db } from '../firebase';
import { rejectionMessage } from '../utils/rejectionMessage';

export const TECH_PROFILE_LOAD_ERROR = 'Technician profile could not load.';

export const SIGN_OUT_FAILED = 'Could not sign out.';

export const TOKEN_READ_FAILED = 'Could not read the sign-in token.';

export function techProfileListenMessage(snapshotError) {
    const detail = rejectionMessage(snapshotError, '');
    return detail ? `${TECH_PROFILE_LOAD_ERROR} ${detail}` : TECH_PROFILE_LOAD_ERROR;
}

export function signOutFailureMessage(signOutError) {
    const detail = rejectionMessage(signOutError, '');
    return detail ? `${SIGN_OUT_FAILED} ${detail}` : SIGN_OUT_FAILED;
}

export function tokenReadFailureMessage(tokenError) {
    const detail = rejectionMessage(tokenError, '');
    return detail ? `${TOKEN_READ_FAILED} ${detail}` : TOKEN_READ_FAILED;
}

function isTokenReadFailure(message) {
    return message === TOKEN_READ_FAILED || (typeof message === 'string' && message.startsWith(`${TOKEN_READ_FAILED} `));
}

const defaultFirebaseContext = {
    user: null,
    error: null,
};

export const AuthContext = createContext(defaultFirebaseContext);

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [error, setError] = useState(null);
    const signedInUid = useRef(null);

    const [tech, setTech] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);

    const [userInitialized, setUserInitialized] = useState(false);
    const claimsGeneration = useRef(0);
    // A sign-out started by a run that was cleaned up must not clear the newer session.
    const ignoreSignedOut = useRef(false);

    const regionId = user?.claims?.regionId;
    const collectionPrefix = useMemo(() => (regionId ? `/regions/${regionId}` : null), [regionId]);
    const jobsRef = useMemo(() => (collectionPrefix ? db.collection(`${collectionPrefix}/jobs`) : null), [
        collectionPrefix,
    ]);
    const techsRef = useMemo(() => (collectionPrefix ? db.collection(`${collectionPrefix}/techs`) : null), [
        collectionPrefix,
    ]);
    const shopsRef = useMemo(() => (collectionPrefix ? db.collection(`${collectionPrefix}/shops`) : null), [
        collectionPrefix,
    ]);

    const refreshClaims = async (u, force = false) => {
        if (!u && ignoreSignedOut.current) {
            ignoreSignedOut.current = false;
            return;
        }
        const generation = ++claimsGeneration.current;
        const stillCurrent = (startedUid) =>
            generation === claimsGeneration.current &&
            !!auth.currentUser &&
            auth.currentUser.uid === startedUid;

        if (!u) {
            signedInUid.current = null;
            setUser(null);
            setUserInitialized(true);
            return;
        }
        const startedUid = u.uid;
        try {
            let idTokenResult = await u.getIdTokenResult(force);
            if (!stillCurrent(startedUid)) {
                return;
            }
            // Cached tokens can omit regionId. Force once before publishing a session that would sign out.
            if (!idTokenResult?.claims?.regionId && !force) {
                idTokenResult = await u.getIdTokenResult(true);
                if (!stillCurrent(startedUid)) {
                    return;
                }
            }
            const uidChanged = signedInUid.current !== u.uid;
            if (uidChanged) {
                signedInUid.current = u.uid;
            }
            setError((current) => {
                if (uidChanged || isTokenReadFailure(current)) {
                    return null;
                }
                return current;
            });
            setUser((prev) => {
                const { claims } = idTokenResult;
                if (
                    prev &&
                    prev.uid === u.uid &&
                    prev.claims?.regionId === claims.regionId &&
                    prev.claims?.admin === claims.admin &&
                    prev.claims?.shopUser === claims.shopUser &&
                    prev.claims?.shopId === claims.shopId
                ) {
                    return prev;
                }
                return { ...u, claims };
            });
        } catch (tokenError) {
            if (!stillCurrent(startedUid)) {
                return;
            }
            const currentUid = auth.currentUser.uid;
            setError(tokenReadFailureMessage(tokenError));
            // Keep the user already in state when this is still their Firebase uid.
            // setUser(null) unmounts signed-in routes and drops in-progress forms.
            setUser((prev) => (prev?.uid === currentUid ? prev : null));
        }
        if (generation !== claimsGeneration.current) {
            return;
        }
        setUserInitialized(true);
    };

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((u) => refreshClaims(u, true));
        const refreshOnFocus = () => {
            if (auth.currentUser) refreshClaims(auth.currentUser, false);
        };
        window.addEventListener('focus', refreshOnFocus);
        return () => {
            unsubscribe();
            window.removeEventListener('focus', refreshOnFocus);
        };
    }, []);

    useEffect(() => {
        let active = true;
        const runGeneration = claimsGeneration.current;
        let signOutStarted = false;
        let signOutSettled = false;
        const signOutStillCurrent = () => active && runGeneration === claimsGeneration.current;

        const signOutOrReport = (prefix) => {
            if (!signOutStillCurrent()) {
                return;
            }
            signOutStarted = true;
            auth.signOut().then(
                () => {
                    signOutSettled = true;
                },
                (signOutError) => {
                    signOutSettled = true;
                    if (!signOutStillCurrent()) {
                        return;
                    }
                    console.error(SIGN_OUT_FAILED, signOutError);
                    const failure = signOutFailureMessage(signOutError);
                    setError(prefix ? `${prefix} ${failure}` : failure);
                    setIsInitialized(true);
                },
            );
        };

        const stop = (unsubscribe) => {
            active = false;
            if (signOutStarted && !signOutSettled) {
                ignoreSignedOut.current = true;
            }
            if (unsubscribe) {
                unsubscribe();
            }
        };

        if (!userInitialized) {
            return () => {
                stop();
            };
        }

        if (user?.claims?.regionId) {
            const unsubscribe = db
                .collection(`/regions/${user.claims.regionId}/techs`)
                .doc(user.uid)
                .onSnapshot(
                    (techDoc) => {
                        if (techDoc.exists) {
                            setTech({
                                ...techDoc.data(),
                                id: techDoc.id,
                            });
                            setIsInitialized(true);
                        } else {
                            signOutOrReport();
                        }
                    },
                    (snapshotError) => {
                        console.error('Unable to load technician profile', snapshotError);
                        setTech(null);
                        setIsInitialized(true);
                        const profileMessage = techProfileListenMessage(snapshotError);
                        setError(profileMessage);
                        signOutOrReport(profileMessage);
                    },
                );
            return () => {
                stop(unsubscribe);
            };
        }

        setTech(null);
        setIsInitialized(true);

        if (user) {
            signOutOrReport();
        }

        return () => {
            stop();
        };
    }, [user, userInitialized]);

    const techId = tech?.id ?? null;

    return (
        <AuthContext.Provider
            value={{
                user,
                error,
                tech,
                techId,
                initialized: isInitialized,
                collectionPrefix,
                jobsRef,
                techsRef,
                shopsRef,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

UserProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
